import { isPosthogEnabled } from "config";
import { getPosthogIfReady } from "config/posthog-runtime";

const ACQUISITION_ATTRIBUTION_STORAGE_KEY = "rivet_acquisition_attribution";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

const GOOGLE_ADS_CLICK_KEYS = ["gclid", "gbraid", "wbraid"] as const;

const OPENAI_ADS_CLICK_KEYS = ["oppref"] as const;

export const ACQUISITION_ATTRIBUTION_PARAM_KEYS = [
  ...UTM_KEYS,
  ...GOOGLE_ADS_CLICK_KEYS,
  ...OPENAI_ADS_CLICK_KEYS,
] as const;

export type AcquisitionAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  oppref?: string;
  referring_domain?: string;
};

type StoredAcquisitionAttribution = AcquisitionAttribution & {
  captured_at?: string;
};

function isNonEmptyString(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function readReferringDomain(): string | undefined {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return undefined;
  }
  if (!isNonEmptyString(document.referrer)) {
    return undefined;
  }
  try {
    const referrerHost = new URL(document.referrer).hostname;
    if (referrerHost === window.location.hostname) {
      return undefined;
    }
    return referrerHost;
  } catch {
    return undefined;
  }
}

function readAttributionParam(
  params: URLSearchParams,
  key: (typeof ACQUISITION_ATTRIBUTION_PARAM_KEYS)[number]
): string | undefined {
  const value = params.get(key)?.trim();
  return isNonEmptyString(value) ? value : undefined;
}

export function readAcquisitionAttributionFromUrl(): AcquisitionAttribution {
  if (typeof window === "undefined") {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const attribution: AcquisitionAttribution = {};

  for (const key of ACQUISITION_ATTRIBUTION_PARAM_KEYS) {
    const value = readAttributionParam(params, key);
    if (value) {
      attribution[key] = value;
    }
  }

  const referringDomain = readReferringDomain();
  if (referringDomain) {
    attribution.referring_domain = referringDomain;
  }

  return attribution;
}

function readStoredAcquisitionAttribution(): AcquisitionAttribution {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(ACQUISITION_ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as StoredAcquisitionAttribution;
    const attribution: AcquisitionAttribution = {};
    for (const key of ACQUISITION_ATTRIBUTION_PARAM_KEYS) {
      const value = parsed[key];
      if (isNonEmptyString(value)) {
        attribution[key] = value;
      }
    }
    if (isNonEmptyString(parsed.referring_domain)) {
      attribution.referring_domain = parsed.referring_domain;
    }
    return attribution;
  } catch {
    return {};
  }
}

function hasAttributionValues(attribution: AcquisitionAttribution): boolean {
  return Object.keys(attribution).length > 0;
}

export function persistAcquisitionAttributionFromUrl(): void {
  if (typeof window === "undefined") {
    return;
  }
  const fromUrl = readAcquisitionAttributionFromUrl();
  if (!hasAttributionValues(fromUrl)) {
    return;
  }
  const payload: StoredAcquisitionAttribution = {
    ...fromUrl,
    captured_at: new Date().toISOString(),
  };
  try {
    sessionStorage.setItem(ACQUISITION_ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable in private browsing
  }
}

function readPosthogAttributionFallback(): AcquisitionAttribution {
  if (!isPosthogEnabled || typeof window === "undefined") {
    return {};
  }
  const posthog = getPosthogIfReady();
  if (!posthog) {
    return {};
  }
  try {
    const readProperty = (key: string): string | undefined => {
      const value = posthog.get_property(key);
      return isNonEmptyString(typeof value === "string" ? value : undefined)
        ? value.trim()
        : undefined;
    };

    const attribution: AcquisitionAttribution = {};
    const utmSource = readProperty("utm_source") ?? readProperty("$initial_utm_source");
    const utmMedium = readProperty("utm_medium") ?? readProperty("$initial_utm_medium");
    const utmCampaign = readProperty("utm_campaign") ?? readProperty("$initial_utm_campaign");
    const utmContent = readProperty("utm_content") ?? readProperty("$initial_utm_content");
    const utmTerm = readProperty("utm_term") ?? readProperty("$initial_utm_term");
    const gclid = readProperty("gclid");
    const gbraid = readProperty("gbraid");
    const wbraid = readProperty("wbraid");
    const referringDomain =
      readProperty("referring_domain") ??
      readProperty("$referring_domain") ??
      readProperty("$initial_referring_domain");

    if (utmSource) attribution.utm_source = utmSource;
    if (utmMedium) attribution.utm_medium = utmMedium;
    if (utmCampaign) attribution.utm_campaign = utmCampaign;
    if (utmContent) attribution.utm_content = utmContent;
    if (utmTerm) attribution.utm_term = utmTerm;
    if (gclid) attribution.gclid = gclid;
    if (gbraid) attribution.gbraid = gbraid;
    if (wbraid) attribution.wbraid = wbraid;
    if (referringDomain) attribution.referring_domain = referringDomain;

    return attribution;
  } catch {
    return {};
  }
}

export function getAcquisitionAttributionForEvent(): AcquisitionAttribution {
  const fromUrl = readAcquisitionAttributionFromUrl();
  const stored = readStoredAcquisitionAttribution();
  const fromPosthog = readPosthogAttributionFallback();
  return {
    ...fromPosthog,
    ...stored,
    ...fromUrl,
  };
}
